import React, { createContext } from 'react';
import * as R from 'ramda';

import { isEmptyFieldFilter } from '@atom/components/common/workOrdersFilters/fieldFilterUtilities';
import { WorkOrdersInputState } from '@atom/hooks/useWorkOrdersFilters';
import {
  FieldFilter,
  TaskFieldsFilter,
  TaskFilter,
  WorkOrder,
} from '@atom/types/work';
import { WorkOrderColumn } from '@atom/types/workColumns';
import { CustomFieldFilter, WorkFilter } from '@atom/types/workFilters';
import { isNilOrEmpty } from '@atom/utilities/validationUtilities';

export enum FilterSection {
  WORK = 'WORK',
  TASK = 'TASK',
  TEAM = 'TEAM',
  INVENTORY = 'INVENTORY',
  LOCATIONS = 'LOCATIONS',
}

export interface Context {
  workOrders: WorkOrder[];
  customFieldFilters?: CustomFieldFilter[];
  workOrderColumns: WorkOrderColumn[];
  refetchColumns: () => Promise<any>;
  totalCount: number;
  loading: boolean;
  workFilters: WorkFilter[];
  refetchWorkFilters: () => Promise<any>;
  loadingFilters: boolean;
  refetch: () => Promise<any>;
  input: WorkOrdersInputState;
  setInput: (input: WorkOrdersInputState) => void;
  workOrdersInputCart: WorkOrdersInputState;
  dispatch: React.Dispatch<WorkOrdersInputActions>;
  filtersDisabled?: boolean;
  collapsedSections: Set<FilterSection>;
  setCollapsedSections: (collapsedSections: Set<FilterSection>) => void;
  getWorkFilters: (params: any) => void;
}

export const initialState: Context = {
  workOrders: [],
  workOrderColumns: [],
  customFieldFilters: [],
  refetchColumns: () => Promise.resolve(),
  totalCount: 0,
  loading: false,
  workFilters: [],
  refetchWorkFilters: () => Promise.resolve(),
  loadingFilters: false,
  input: {},
  setInput: () => {},
  workOrdersInputCart: {},
  dispatch: () => {},
  refetch: () => Promise.resolve(),
  filtersDisabled: false,
  collapsedSections: new Set([]),
  setCollapsedSections: () => {},
  getWorkFilters: (params: any) => {},
};

export default createContext<Context>(initialState);

export enum WorkOrdersInputActionTypes {
  SET_WORK_ORDERS_INPUT = 'SET_WORK_ORDERS_INPUT',
  UPDATE_WORK_ORDERS_INPUT_PROPERTY = 'UPDATE_WORK_ORDERS_INPUT_PROPERTY',
  UPDATE_TASKS_INPUT_PROPERTY = 'UPDATE_TASKS_INPUT_PROPERTY',
  UPDATE_WORK_ORDERS_FIELD = 'UPDATE_WORK_ORDERS_FIELD',
  UPDATE_TASKS_COMMON_FIELD = 'UPDATE_TASKS_COMMON_FIELD',
  UPDATE_TASKS_FIELD = 'UPDATE_TASKS_FIELD',
}

interface SetWorkOrdersInput {
  type: WorkOrdersInputActionTypes.SET_WORK_ORDERS_INPUT;
  data: WorkOrdersInputState;
}

interface UpdateWorkOrdersInputProperty {
  type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY;
  data: {
    property: keyof WorkOrdersInputState;
    value: any;
  };
}

interface UpdateTasksInputProperty {
  type: WorkOrdersInputActionTypes.UPDATE_TASKS_INPUT_PROPERTY;
  data: {
    property: keyof TaskFilter;
    value: any;
  };
}

// fieldFilter carries the merged filter for a single field -
// value, values, or valueStart / valueEnd
interface UpdateWorkOrdersField {
  type: WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_FIELD;
  data: {
    fieldFilter: FieldFilter;
  };
}

interface UpdateTasksCommonField {
  type: WorkOrdersInputActionTypes.UPDATE_TASKS_COMMON_FIELD;
  data: {
    fieldFilter: FieldFilter;
  };
}

interface UpdateTasksField {
  type: WorkOrdersInputActionTypes.UPDATE_TASKS_FIELD;
  data: {
    taskId: string;
    fieldFilter: FieldFilter;
  };
}

type WorkOrdersInputActions =
  | SetWorkOrdersInput
  | UpdateWorkOrdersInputProperty
  | UpdateTasksInputProperty
  | UpdateWorkOrdersField
  | UpdateTasksCommonField
  | UpdateTasksField;

// updates a single property on the workOrder input.
const updateWorkOrdersInputProperty = (
  state: WorkOrdersInputState,
  action: UpdateWorkOrdersInputProperty,
) => {
  return {
    ...state,
    [action.data.property]: action.data.value,
  };
};

// updates a single property on the tasks input.
const updateTasksInputProperty = (
  state: WorkOrdersInputState,
  action: UpdateTasksInputProperty,
) => {
  return {
    ...state,
    tasks: {
      ...state.tasks,
      [action.data.property]: action.data.value,
    },
  };
};

// replaces the filter for a single field, dropping it entirely once the user
// has cleared every value on it
const upsertFieldFilter = (
  fields: FieldFilter[],
  fieldFilter: FieldFilter,
): FieldFilter[] => {
  if (isEmptyFieldFilter(fieldFilter)) {
    return fields.filter(field => field.id !== fieldFilter.id);
  }

  return fields.some(field => field.id === fieldFilter.id)
    ? fields.map(field => (field.id === fieldFilter.id ? fieldFilter : field))
    : [...fields, fieldFilter];
};

// updates a common field filter on the tasks input.
const updateTasksCommonField = (
  state: WorkOrdersInputState,
  action: UpdateTasksCommonField,
) => {
  return {
    ...state,
    tasks: {
      ...state.tasks,
      commonFields: upsertFieldFilter(
        state?.tasks?.commonFields || [],
        action.data.fieldFilter,
      ),
    },
  };
};

// updates a single work field filter
const updateWorkOrdersField = (
  state: WorkOrdersInputState,
  action: UpdateWorkOrdersField,
) => {
  return {
    ...state,
    fields: upsertFieldFilter(state?.fields || [], action.data.fieldFilter),
  };
};

// Removes tasks that have empty filters
const cleanTasks = (tasks: TaskFieldsFilter[]): TaskFieldsFilter[] => {
  return tasks.filter(task => !isNilOrEmpty(task.fields));
};

// updates a single task field filter
const updateTasksField = (
  state: WorkOrdersInputState,
  action: UpdateTasksField,
) => {
  const { taskId, fieldFilter } = action.data;

  const currentTasks: TaskFieldsFilter[] = R.pathOr(
    [],
    ['tasks', 'each'],
    state,
  );

  const hasTask = currentTasks.some(task => task.id === taskId);

  const updatedTasks = hasTask
    ? currentTasks.map(task =>
        task.id === taskId
          ? {
              ...task,
              fields: upsertFieldFilter(task.fields || [], fieldFilter),
            }
          : task,
      )
    : [
        ...currentTasks,
        { id: taskId, fields: upsertFieldFilter([], fieldFilter) },
      ];

  return {
    ...state,
    tasks: {
      ...state.tasks,
      each: cleanTasks(updatedTasks),
    },
  };
};

export const reducer = (
  state: WorkOrdersInputState,
  action: WorkOrdersInputActions,
): WorkOrdersInputState => {
  switch (action.type) {
    case WorkOrdersInputActionTypes.SET_WORK_ORDERS_INPUT:
      return action.data;
    case WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_INPUT_PROPERTY:
      return updateWorkOrdersInputProperty(state, action);
    case WorkOrdersInputActionTypes.UPDATE_TASKS_INPUT_PROPERTY:
      return updateTasksInputProperty(state, action);
    case WorkOrdersInputActionTypes.UPDATE_WORK_ORDERS_FIELD:
      return updateWorkOrdersField(state, action);
    case WorkOrdersInputActionTypes.UPDATE_TASKS_COMMON_FIELD:
      return updateTasksCommonField(state, action);
    case WorkOrdersInputActionTypes.UPDATE_TASKS_FIELD:
      return updateTasksField(state, action);
    default: {
      return state;
    }
  }
};

export const getTaskCommonField = (
  workOrdersInputCart: WorkOrdersInputState,
  fieldId?: string,
): any => {
  return (
    R.find(R.propEq(fieldId, 'id'))(
      R.pathOr([], ['tasks', 'commonFields'], workOrdersInputCart),
    ) || {}
  );
};

export const getTaskField = (
  workOrdersInputCart: WorkOrdersInputState,
  fieldId?: string,
  taskId?: string,
): any => {
  const taskIndex = R.findIndex(R.propEq(taskId, 'id'))(
    R.pathOr([], ['tasks', 'each'], workOrdersInputCart),
  );

  if (taskIndex < 0) {
    return {};
  }

  const fieldIndex = R.findIndex(R.propEq(fieldId, 'id'))(
    (R.view(
      R.lensPath(['tasks', 'each', taskIndex, 'fields']),
      workOrdersInputCart,
    ) as any) || [],
  );

  if (fieldIndex < 0) {
    return {};
  }

  const field = R.view(
    R.lensPath(['tasks', 'each', taskIndex, 'fields', fieldIndex]),
    workOrdersInputCart,
  );

  return field || {};
};
